<template>
  <b-container>
    <b-row v-if="showTitle" class="mt-3 mb-2">
      <b-col>
        <b>{{ $t('scoringProfiles.selectedFilters') }}</b>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <cancellable-badge
          v-for="chip in chips"
          :key="chip.id"
          class="active-filter-chip"
          :text="chip.text"
          variant="primary"
          :can-remove="canRemove"
          @cancel="onCancel(chip)"
        ></cancellable-badge>
        <span v-if="chips.length === 0" class="text-muted">{{ $t('common.none') }}</span>

        <template v-if="hasExcludedProducts">
          <div>
            <cancellable-badge
              variant="secondary"
              key="manuallyExcludeFromPerimeter"
              :icon-name="'exclamation-circle'"
              :can-remove="false"
              :text="$t('scoringProfiles.insight.manuallyExcludedFromPerimeterBadge')"
            ></cancellable-badge>

            <custom-tooltip
              :css-class="['pointer']"
              :section="'scoring-profiles-tab-filters'"
              :tooltipText="$t('scoringProfiles.insight.manuallyExcludedFromPerimeterTooltip')"
            ></custom-tooltip>
          </div>
        </template>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
// "Filtri selezionati" chip list for the catalog-driven scoring profile authoring page.
// Same chip-formatting rules as search's ActiveFilters.vue (perimeter/company/date-range/
// array/boolean/range value labels), reading the `scoringProfilesFilters` module's single
// `filters` array instead of search's pendingFilters, and without the "N products filtered" line
// (not applicable outside a live-search context).
import { createNamespacedHelpers, mapGetters } from 'vuex'
import CancellableBadge from '../Lib/CancellableBadge'
import CustomTooltip from '../Lib/CustomTooltip.vue'
import { rhpToYears } from '../../utils/formatter'

const { mapState, mapActions } = createNamespacedHelpers('scoringProfilesFilters')

const RANGE_UNITS = {
  RhpRange: 'years',
  TotalCostsRange: 'percent',
  RiyRange: 'percent',
  InsuredEventRange: 'percent',
  TMRetailInvestorAge: 'years',
  'AdditionalInfo.PremiumPayedPeriod': 'years',
  TMLimitedCapitalLossLevel: 'roundPercent',
  'AdditionalInfo.AllocationToSegregatedFund': 'roundPercent',
  'AdditionalInfo.AllocationToUnitLinked': 'roundPercent',
  'AdditionalInfo.IntermediaryCosts': 'roundPercent',
  'AdditionalInfo.SinglePremiumAmount': 'currency',
  'AdditionalInfo.RecurrentPremiumAmount': 'currency',
  CapitalProtection: 'roundPercent',
  BarrierLevel: 'roundPercent',
  CouponBarrierLevel: 'roundPercent',
}

const RANGE_NULL_PLACEHOLDERS = {
  RhpRange: { to: '> 20y' },
  TotalCostsRange: { from: '< 0%', to: '> 10%' },
  RiyRange: { from: '< 0%', to: '> 5%' },
  InsuredEventRange: { from: '< 0%', to: '> 5%' },
}

const RANGE_DEFAULT_BOUNDS = {
  TMRetailInvestorAge: { min: 1, max: 100 },
  TMLimitedCapitalLossLevel: { min: 0, max: 1 },
  'AdditionalInfo.SinglePremiumAmount': { min: 0, max: 500000 },
  'AdditionalInfo.RecurrentPremiumAmount': { min: 0, max: 10000 },
  'AdditionalInfo.AllocationToSegregatedFund': { min: 0, max: 1 },
  'AdditionalInfo.AllocationToUnitLinked': { min: 0, max: 1 },
  'AdditionalInfo.PremiumPayedPeriod': { min: 1, max: 30 },
  'AdditionalInfo.IntermediaryCosts': { min: 0, max: 1 },
  CapitalProtection: { min: 0, max: 1 },
  BarrierLevel: { min: 0, max: 1 },
  CouponBarrierLevel: { min: 0, max: 1 },
}

const DATE_RANGE_KEYS = ['IssueDate', 'MaturityDate']

export default {
  name: 'selectedFilters',
  components: { CancellableBadge, CustomTooltip },
  props: {
    // Read-only rendering (scoring profile details page): chips are shown without the remove affordance.
    canRemove: {
      type: Boolean,
      default: true,
    },
    // The details page provides its own section heading, so the built-in one can be suppressed.
    showTitle: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    ...mapState({
      filters: (state) => state.filters,
      catalog: (state) => state.catalog,
    }),
    ...mapGetters('scoringProfilesCoverageDetail', ['getCoverageExcludedProducts']),
    hasExcludedProducts() {
      return this.getCoverageExcludedProducts.length > 0
    },
    filterKeyOrder() {
      const groupFilterKeys = (this.catalog.groups || []).flatMap((group) => group.filters.map((f) => f.filterKey))
      const ungroupedKeys = (this.catalog.ungrouped || []).map((f) => f.filterKey)
      return [...groupFilterKeys, ...ungroupedKeys]
    },
    allCatalogFilters() {
      const grouped = (this.catalog.groups || []).flatMap((group) => group.filters)
      return [...grouped, ...(this.catalog.ungrouped || [])]
    },
    orderedFilters() {
      return [...this.filters].sort((a, b) => {
        const indexA = this.filterKeyOrder.indexOf(a.key)
        const indexB = this.filterKeyOrder.indexOf(b.key)
        return (indexA === -1 ? this.filterKeyOrder.length : indexA) - (indexB === -1 ? this.filterKeyOrder.length : indexB)
      })
    },
    chips() {
      return this.orderedFilters.flatMap((entry) => {
        if (entry.key === 'CompanyFilter') {
          const text = this.companyFilterChipText(entry.value)
          return text ? [{ id: entry.key, key: entry.key, item: null, text }] : []
        }
        if (entry.key === 'Perimeter') {
          const text = this.perimeterChipText(entry.value)
          return text ? [{ id: entry.key, key: entry.key, item: null, text }] : []
        }
        if (DATE_RANGE_KEYS.includes(entry.key) && entry.value && typeof entry.value === 'object') {
          return ['from', 'to']
            .filter((side) => entry.value[side] !== null && entry.value[side] !== undefined)
            .map((side) => ({
              id: entry.key + ':' + side,
              key: entry.key,
              rangeSide: side,
              item: null,
              text: this.filterLabel(entry.key) + ' (' + this.$t('search.range' + (side === 'from' ? 'From' : 'To')) + '): ' + this.$d(new Date(entry.value[side])),
            }))
        }
        if (Array.isArray(entry.value)) {
          return entry.value.map((item, index) => ({
            id: entry.key + ':' + index,
            key: entry.key,
            item,
            text: this.filterLabel(entry.key) + ': ' + this.valueLabel(entry.key, item),
          }))
        }
        return [
          {
            id: entry.key,
            key: entry.key,
            item: null,
            text: this.filterLabel(entry.key) + ': ' + this.valueLabel(entry.key, entry.value),
          },
        ]
      })
    },
  },
  methods: {
    ...mapActions(['removeFilter', 'removeFilterValueItem', 'removeFilterRangeSide']),
    onCancel(chip) {
      if (chip.rangeSide) {
        this.removeFilterRangeSide({ key: chip.key, side: chip.rangeSide })
        return
      }
      if (chip.item === null) {
        this.removeFilter(chip.key)
        return
      }
      this.removeFilterValueItem({ key: chip.key, item: chip.item })
    },
    filterLabel(key) {
      const labelKey = 'search.filters.' + key
      return this.$te(labelKey) ? this.$t(labelKey) : key
    },
    perimeterChipText(value) {
      if (!value || value.mode !== 'specific_date') {
        return null
      }
      const inPerimeterLabel = value.in_perimeter
        ? this.$te('search.perimeterInPerimeter.' + value.in_perimeter)
          ? this.$t('search.perimeterInPerimeter.' + value.in_perimeter)
          : value.in_perimeter
        : ''
      const inPerimeterPart = inPerimeterLabel
        ? ', ' + this.$t('search.label.perimeterConfiguration.perimeterSdOptions') + ': ' + inPerimeterLabel
        : ''
      const dateLabel = value.date ? this.$d(new Date(value.date)) : ''
      return this.$t('search.label.perimeterConfiguration.perimeterSingleDateBadge') + ': ' + dateLabel + inPerimeterPart
    },
    companyFilterChipText(value) {
      if (!value) {
        return null
      }
      const mode = value.market_products === 'none' ? 'only' : value.products_in_test === 'include' ? 'include' : 'exclude'
      const labelKey = mode === 'only' ? 'pit.onlyTestProducts' : mode === 'include' ? 'pit.includeTestProducts' : 'pit.excludeTestProducts'
      return this.filterLabel('CompanyFilter') + ': ' + this.$t(labelKey)
    },
    intervalItemLabel(key, item) {
      const filter = this.allCatalogFilters.find((f) => f.filterKey === key)
      const options = (filter && filter.options) || []
      const index = options.findIndex((o) => o.from === item.from && o.to === item.to)
      if (key === 'TMTimeHorizonIntervals') {
        const codes = ['1', '2', '4', '8']
        const code = codes[index]
        const n = code === '8' ? item.from + 1 : item.from
        return this.$t('search.filterSelectValues.timeHorizon.' + code, { n, z: item.to })
      }
      return this.$t('search.filterSelectValues.DIP.additionalInformation.aiRedeemTerm.' + index)
    },
    valueLabel(key, value) {
      const orEmpty = (v) => (v === null || v === undefined ? '' : v)
      // IssuerCodes is not a compatibility filter (search-only, per okv-patterns.md), so this key
      // never appears in scoringProfilesFilters' catalog — no issuer-name lookup needed here.
      if (typeof value === 'boolean') {
        return this.$t(value ? 'search.booleanTrue' : 'search.booleanFalse')
      }
      if ((key === 'TMTimeHorizonIntervals' || key === 'AdditionalInfo.RedeemTermIntervals') && value && typeof value === 'object') {
        return this.intervalItemLabel(key, value)
      }
      if (value && typeof value === 'object') {
        if ('from' in value || 'to' in value) {
          const unit = RANGE_UNITS[key]
          const placeholders = RANGE_NULL_PLACEHOLDERS[key] || {}
          const defaultBounds = RANGE_DEFAULT_BOUNDS[key] || {}
          const fmt = (v) => {
            if (unit === 'years') return rhpToYears(v)
            if (unit === 'percent') return this.$n(v, 'percent')
            if (unit === 'roundPercent') return Math.round(v * 100) + '%'
            if (unit === 'currency') return this.$n(v, 'currency')
            return v
          }
          if (value.from !== null && value.from !== undefined && value.from === value.to) {
            return unit ? fmt(value.from) : String(value.from)
          }
          if (unit) {
            const boundKey = { from: 'min', to: 'max' }
            const sideText = (v, side) => {
              if (v !== null && v !== undefined) return fmt(v)
              if (placeholders[side]) return placeholders[side]
              if (boundKey[side] in defaultBounds) return fmt(defaultBounds[boundKey[side]])
              return ''
            }
            return `${sideText(value.from, 'from')} - ${sideText(value.to, 'to')}`
          }
          return `${orEmpty(value.from)} - ${orEmpty(value.to)}`
        }
        return JSON.stringify(value)
      }
      const optionKey = 'search.options.' + key + '.' + value
      return this.$te(optionKey) ? this.$t(optionKey) : String(value)
    },
  },
}
</script>
